import type { CSSProperties, ReactNode } from 'react';
import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { glossary } from '@sorare/core/src/lib/glossary';

const StyledSection = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
`;

const SectionIcon = styled.span`
  display: contents;
  & svg {
    color: var(--icon-color, currentColor);
  }
`;

const Chevron = styled(FontAwesomeIcon)`
  transition: transform 0.3s;
  [data-state='open'] & {
    transform: rotate(180deg);
  }
`;

type Props = {
  icon: ReactNode;
  iconColor?: string;
  title: ReactNode;
  extraTitleElement?: ReactNode;
  children: ReactNode;
};

export const MissionSection = ({
  icon,
  iconColor,
  title,
  extraTitleElement,
  children,
}: Props) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(true);
  const contentId = useId();

  return (
    <StyledSection>
      <SBHorizontal>
        <Button
          size="small"
          color="tertiary"
          brand
          startIcon={<SectionIcon>{icon}</SectionIcon>}
          endIcon={
            <Chevron
              icon={iconChevronDown}
              aria-label={formatMessage(
                expanded ? glossary.showLess : glossary.showMore
              )}
            />
          }
          data-state={expanded ? 'open' : 'closed'}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(prev => !prev)}
          style={
            iconColor
              ? ({ '--icon-color': iconColor } as CSSProperties)
              : undefined
          }
        >
          {title}
        </Button>
        {extraTitleElement}
      </SBHorizontal>
      <Collapse open={expanded} gap={1.5}>
        <Vertical gap={1.5} id={contentId}>
          {children}
        </Vertical>
      </Collapse>
    </StyledSection>
  );
};
